/**
 * Settings — what only the signed-in person can change: their own password.
 *
 * ── ADAPTED FROM THE APPROVED UI, NOT REBUILT ─────────────────────────────
 * `reconcolio_dummy_ui_1_9_2026/frontend/src/pages/Firm.tsx` is the approved
 * source. Three things it carried are not here:
 *
 *   1. The audit trail (its `Activity` export) — this task gives it a
 *      screen of its own at `/activity` (`pages/Activity.tsx`), reading the
 *      real `GET /activity`, rather than duplicating it here.
 *   2. "Your details" — a form saving `name`/`jobTitle` via
 *      `PATCH /users/{user.id}`. That route is real, but it is
 *      administrators-only end to end (`usersRouter`'s `adminOnly` guard,
 *      `backend/src/routes/users.ts`): an AUDITOR or DATA_ENTRY calling it
 *      on their OWN id still gets a 403. There is no separate
 *      "update my own profile" endpoint in this API. Rather than ship a form
 *      that works for one of three roles and fails for the other two, this
 *      screen shows those details read-only and drops the editable form.
 *   3. The data-reset strip at the foot of the approved screen, which
 *      cleared the approved tree's local, browser-only data layer — this
 *      build has no such layer to clear, and inventing one would violate
 *      this plan's own hard constraint against fabricated data or copy.
 *
 * What is real and kept: changing your own password
 * (`POST /auth/change-password`, requiring the current one — a stolen
 * session should not be enough on its own).
 *
 * The password form is now `FormTextField`/`zodResolver`, wired to real
 * server field errors via `setError` — the previous version's
 * `changePassword.fields` was always `{}`, so a field-level error from the
 * API (a wrong current password) never actually reached either input.
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Typography } from '@mui/material';
import { ApiError } from '../api/errors';
import { useToast } from '../provider/toast';
import { useChangePasswordMutation } from '../queries/firm';
import { useAuth } from '../provider/auth';
import { Card, Detail, ErrorNotice } from '../shared/ui';
import FormTextField from '../shared/components/form/text-field';
import FormErrorBox from '../shared/components/form/error';
import { PageHeader } from '../provider/pageTitle';
import { ROLE_LABEL } from '../shared/roles';
import {
	changePasswordFormSchema,
	type ChangePasswordForm,
} from '../schemas/forms';

export default function Settings() {
	const { user } = useAuth();
	const toast = useToast();
	const changePasswordMutation = useChangePasswordMutation();

	const {
		control,
		handleSubmit,
		setError,
		reset,
		watch,
		formState: { errors },
	} = useForm<ChangePasswordForm>({
		resolver: zodResolver(changePasswordFormSchema),
		mode: 'onChange',
		defaultValues: { currentPassword: '', newPassword: '' },
	});
	const currentPassword = watch('currentPassword');
	const newPassword = watch('newPassword');

	const submit = handleSubmit((values) => {
		changePasswordMutation.mutate(values, {
			onSuccess: () => {
				reset({ currentPassword: '', newPassword: '' });
				toast('ok', 'Password changed.');
			},
			onError: (error) => {
				if (!(error instanceof ApiError)) return;
				for (const [field, message] of Object.entries(error.fields)) {
					if (field in values)
						setError(field as keyof ChangePasswordForm, {
							message,
						});
				}
			},
		});
	});

	if (!user) return null;

	return (
		<Box className="stack">
			<PageHeader title="My account" subtitle={user.firmName} />

			<Box className="grid g2">
				<Card title="Your details">
					<Box className="stack">
						<Detail label="Name">{user.name}</Detail>
						<Detail label="Email">{user.email}</Detail>
						<Detail label="Role">{ROLE_LABEL[user.role]}</Detail>
						<Detail label="Job title">
							{user.jobTitle || '—'}
						</Detail>
						<Typography className="small mut">
							An administrator changes these from Administration —
							there is no self-service edit for your own name or
							title in this build.
						</Typography>
					</Box>
				</Card>

				<Card title="Password">
					<Box className="stack">
						<ErrorNotice error={changePasswordMutation.error} />
						<Box>
							<FormTextField
								control={control}
								name="currentPassword"
								label="Current password"
								isRequired
								muiProps={{
									type: 'password',
									autoComplete: 'current-password',
								}}
							/>
							<FormErrorBox errors={[errors.currentPassword]} />
						</Box>
						<Box>
							<FormTextField
								control={control}
								name="newPassword"
								label="New password"
								isRequired
								muiProps={{
									type: 'password',
									autoComplete: 'new-password',
									helperText:
										'At least 8 characters, with a capital letter, a small letter, a number and a special character. Changing it signs you out everywhere else.',
								}}
							/>
							<FormErrorBox errors={[errors.newPassword]} />
						</Box>
						<Button
							loading={changePasswordMutation.isPending}
							type="button"
							variant="outlined"
							disabled={
								changePasswordMutation.isPending ||
								!currentPassword ||
								!newPassword
							}
							onClick={() => void submit()}
						>
							Change password
						</Button>
					</Box>
				</Card>
			</Box>
		</Box>
	);
}
